import { Check, ChevronDown } from "lucide-react";
import * as React from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface SearchableSelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

export interface SearchableSelectOptionGroup {
  heading?: string;
  options: SearchableSelectOption[];
}

interface SearchableSelectProps {
  value?: string;
  defaultValue?: string;
  onValueChange: (value: string) => void;
  options?: SearchableSelectOption[];
  groups?: SearchableSelectOptionGroup[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  preserveOrder?: boolean;
  className?: string;
  triggerClassName?: string;
  popoverClassName?: string;
  id?: string;
  name?: string;
  "data-testid"?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}

const SearchableSelect = React.forwardRef<HTMLButtonElement, SearchableSelectProps>(
  (
    {
      value: controlledValue,
      defaultValue,
      onValueChange,
      options = [],
      groups,
      placeholder = "Selecione...",
      searchPlaceholder = "Buscar...",
      emptyMessage = "Nenhum resultado encontrado.",
      disabled = false,
      preserveOrder = false,
      className,
      triggerClassName,
      popoverClassName,
      id,
      name,
      "data-testid": testId,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const [internalValue, setInternalValue] = React.useState(defaultValue || "");

    const currentValue = controlledValue !== undefined ? controlledValue : internalValue;

    const sortOptions = React.useCallback(
      (opts: SearchableSelectOption[]) =>
        preserveOrder
          ? opts
          : [...opts].sort((a, b) => a.label.localeCompare(b.label, "pt-BR", { numeric: true })),
      [preserveOrder],
    );

    const sortedOptions = React.useMemo(() => sortOptions(options), [options, sortOptions]);

    const sortedGroups = React.useMemo(
      () => groups?.map((g) => ({ ...g, options: sortOptions(g.options) })),
      [groups, sortOptions],
    );

    const allOptions = React.useMemo(() => {
      if (sortedGroups) {
        return sortedGroups.flatMap((g) => g.options);
      }
      return sortedOptions;
    }, [sortedGroups, sortedOptions]);

    const selectedOption = allOptions.find((opt) => opt.value === currentValue);

    const handleSelect = (selectedValue: string) => {
      if (controlledValue === undefined) {
        setInternalValue(selectedValue);
      }
      onValueChange(selectedValue);
      setOpen(false);
    };

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            ref={ref}
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            id={id}
            name={name}
            disabled={disabled}
            data-testid={testId}
            data-placeholder={!selectedOption ? "" : undefined}
            className={cn(
              "flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background data-[placeholder]:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
              triggerClassName,
            )}
          >
            <span className="line-clamp-1 text-left">
              {selectedOption ? (
                <span className="flex items-center gap-2">
                  {selectedOption.icon}
                  {selectedOption.label}
                </span>
              ) : (
                <span className="text-muted-foreground">{placeholder}</span>
              )}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className={cn("p-0 w-[--radix-popover-trigger-width]", popoverClassName, className)}
          align="start"
        >
          <Command>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandList>
              <CommandEmpty>{emptyMessage}</CommandEmpty>
              {sortedGroups ? (
                sortedGroups.map((group, index) => (
                  <React.Fragment key={group.heading ?? index}>
                    {index > 0 && <CommandSeparator />}
                    <CommandGroup heading={group.heading}>
                      {group.options.map((option) => (
                        <CommandItem
                          key={option.value}
                          value={option.label}
                          disabled={option.disabled}
                          onSelect={() => {
                            if (option.disabled) return;
                            handleSelect(option.value);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              currentValue === option.value ? "opacity-100" : "opacity-0",
                            )}
                          />
                          {option.icon && <span className="mr-2">{option.icon}</span>}
                          {option.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </React.Fragment>
                ))
              ) : (
                <CommandGroup>
                  {sortedOptions.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      disabled={option.disabled}
                      onSelect={() => {
                        if (option.disabled) return;
                        handleSelect(option.value);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          currentValue === option.value ? "opacity-100" : "opacity-0",
                        )}
                      />
                      {option.icon && <span className="mr-2">{option.icon}</span>}
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  },
);
SearchableSelect.displayName = "SearchableSelect";

export { SearchableSelect };
